import * as React from 'react';
import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import AliceCarousel from 'react-alice-carousel';
import debounce from 'lodash.debounce';

import { cn, OptionsType } from '@restworld/utils-common';
import { ChipCheckBox } from '@restworld/ui-ds-base';

import { useActiveIndex } from '@restworld/ui-components';
import { BriefcaseIcon } from '@heroicons/react/24/outline';

export interface JobExplorerQuickFiltersProps {
  onQuickFilterChange: (x: string[]) => void;
  activeQuickFilters: string[];
  isDisabled?: boolean;
  quickFilterItems: OptionsType[];
}

export function JobExplorerQuickFilters({
  onQuickFilterChange,
  activeQuickFilters,
  isDisabled,
  quickFilterItems,
}: JobExplorerQuickFiltersProps) {
  const [actives, setActive] = useState<string[]>([]);
  const { activeIndex, onSlideChanged } = useActiveIndex();
  const carouselRef = useRef<AliceCarousel>(null);

  const handleClick = useCallback(
    (id: string, index: number) => {
      setActive((p) => {
        const activeIndexes = [...p].includes(id) ? [...p].filter((v) => v !== id) : [...p, id];
        onQuickFilterChange(activeIndexes);
        return activeIndexes;
      });
      onSlideChanged(index);
    },
    [onQuickFilterChange, onSlideChanged]
  );

  const items = useMemo(
    () =>
      quickFilterItems.map(({ id, label, icon }, index) => {
        const isActive = actives.includes(id);

        return (
          <ChipCheckBox
            key={id}
            checked={isActive}
            id={id}
            label={label}
            disabled={isDisabled}
            onChangeHandler={debounce(() => handleClick(id, index), 150)}
            Icon={BriefcaseIcon}
          />
        );
      }),
    [actives, handleClick, isDisabled, quickFilterItems]
  );

  useEffect(() => {
    if (activeQuickFilters) {
      setActive(activeQuickFilters);
    }
  }, [onSlideChanged, activeQuickFilters]);

  return (
    <Wrapper>
      <AliceCarousel
        mouseTracking
        disableButtonsControls
        disableDotsControls
        keyboardNavigation={false}
        touchTracking
        ref={carouselRef}
        items={items}
        autoWidth
        activeIndex={activeIndex}
        paddingRight={32}
      />
    </Wrapper>
  );
}

export default JobExplorerQuickFilters;
const FilterItem = ({ isDisabled, isActive, className, ...props }: any) => (
  <a
    className={cn(
      'flex flex-row gap-1 items-center px-3 py-2 border border-gray-300 rounded-[8px] max-h-[40px] w-fit',
      isDisabled ? 'cursor-not-allowed pointer-events-none' : '',
      isActive ? 'bg-primary-800' : '',
      className
    )}
    {...props}
  />
);
const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('relative w-full overflow-hidden', className)} {...props} />
);
const LabelEl = ({ isActive, isDisabled, className, ...props }: any) => (
  <h5
    className={cn(
      'text-gray-600 text-sm font-normal leading-5 cursor-pointer',
      isActive ? 'text-white font-semibold' : '',
      isDisabled ? 'text-gray-400' : '',
      className
    )}
    {...props}
  />
);
const IconWrapper = ({ isActive, className, ...props }: any) => (
  <span
    className={cn('rounded-lg flex justify-center items-center', isActive ? 'bg-primary-800' : '', className)}
    {...props}
  />
);
